import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Shop card definitions (literal per card — no factory) ────────────────────

const shop1Def: SlotGroupDefinition = {
  label: 'Shop 1',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/5637bf442d0ac779-96fc22a4-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: "Ruth's Chris Steak House logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "Ruth's Chris Steakhouse",
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '1110 Town Center Blvd.',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        "Ruth's Chris steakhouse is famous for its exceptional food and wine. The restaurant serves the best USDA prime steak sizzling hot. For reservations, please call the leasing office.",
    },
    url: {
      label: 'Website',
      type: 'url',
      defaultValue: 'https://www.ruthschris.com/restaurant-locations/odenton/',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'An on-site retail tenant at this property (restaurant, cafe, salon, daycare, clinic, or similar). Prefer a real business that operates in the building or plaza. leave_empty if none.',
    context:
      'Card 1 of 9 in the Retail shops grid. Logo above name, optional address and short description. Do not reuse shops 2–9.',
  },
};

const shop2Def: SlotGroupDefinition = {
  label: 'Shop 2',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/63beba60b3e579.23518885720-1920w.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Nail Haven Odenton logo' },
    name: { label: 'Name', type: 'text', defaultValue: 'Nail Haven Odenton' },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '1122 Town Center Blvd.',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Beautifying the mind, body & soul.',
    },
    url: {
      label: 'Website',
      type: 'url',
      defaultValue: 'https://www.facebook.com/nailhavenodenton/',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 2 of 9 in the Retail shops grid. Different from shops 1 and 3–9.',
  },
};

const shop3Def: SlotGroupDefinition = {
  label: 'Shop 3',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/6286afb0d44e04.06682133375-removebg-preview-1920w.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'V Cutz Barbershop logo' },
    name: { label: 'Name', type: 'text', defaultValue: 'V Cutz Barbershop' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'VCutz Barbershop is a full-service multicultural barber shop dedicated to providing cutting-edge haircuts and exceptional grooming services while providing an enjoyable, relaxing atmosphere.',
    },
    url: {
      label: 'Website',
      type: 'url',
      defaultValue: 'https://vcutzbarbershop.com/',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 3 of 9 in the Retail shops grid. Different from shops 1–2 and 4–9.',
  },
};

const shop4Def: SlotGroupDefinition = {
  label: 'Shop 4',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/59b892133dfc9920-9b5d2fd8-06c40712-1920w.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Nova Care Rehabilitation logo' },
    name: { label: 'Name', type: 'text', defaultValue: 'Nova Care Rehabilitation' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Every day we help people live better through our individualized outpatient physical and occupational therapy, orthotics, prosthetics and wellness programs.',
    },
    url: { label: 'Website', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 4 of 9 in the Retail shops grid. Different from shops 1–3 and 5–9.',
  },
};

const shop5Def: SlotGroupDefinition = {
  label: 'Shop 5',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/564dee09ab3a2409-1920w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Baltimore Coffee and Tea Company logo',
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: 'Baltimore Coffee and Tea Company',
    },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        "Open early with freshly roasted coffee and delicious breakfast goodies for residents and commuters alike.",
    },
    url: { label: 'Website', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 5 of 9 in the Retail shops grid. Different from shops 1–4 and 6–9.',
  },
};

const shop6Def: SlotGroupDefinition = {
  label: 'Shop 6',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/5637bf0e4b2ff531-91b3c23f-1920w.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Kiddie Academy logo' },
    name: { label: 'Name', type: 'text', defaultValue: 'Kiddie Academy' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Turning every moment into a brilliant learning experience.',
    },
    url: { label: 'Website', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 6 of 9 in the Retail shops grid. Different from shops 1–5 and 7–9.',
  },
};

const shop7Def: SlotGroupDefinition = {
  label: 'Shop 7',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/5e4ea2383fd746.45013942414-1da017b4-1920w.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Doll Wax Salon logo' },
    name: { label: 'Name', type: 'text', defaultValue: 'Doll Wax Salon' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Let us help make your skin your best feature.',
    },
    url: { label: 'Website', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 7 of 9 in the Retail shops grid. Different from shops 1–6 and 8–9.',
  },
};

const shop8Def: SlotGroupDefinition = {
  label: 'Shop 8',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/6286b091007cd6.71933843135-f0ad4d67-1920w.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Quality Stylz logo' },
    name: { label: 'Name', type: 'text', defaultValue: 'Quality Stylz' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Quality Stylz Suites was created to provide larger quality suites at affordable prices.',
    },
    url: { label: 'Website', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 8 of 9 in the Retail shops grid. Different from shops 1–7 and 9.',
  },
};

const shop9Def: SlotGroupDefinition = {
  label: 'Shop 9',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/326c98b6/dms3rep/multi/opt/63bee7c24e6187.36804868129-1920w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Great Scottie Veterinary Clinic logo',
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: 'Great Scottie Veterinary Clinic',
    },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    description: { label: 'Description', type: 'text', defaultValue: '' },
    url: { label: 'Website', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused on-site retail tenant. Prefer a real business at this property. leave_empty if none.',
    context: 'Card 9 of 9 in the Retail shops grid. Different from shops 1–8.',
  },
};

const SHOP_DEFS = [
  { path: 'retailshops.shop1', def: shop1Def },
  { path: 'retailshops.shop2', def: shop2Def },
  { path: 'retailshops.shop3', def: shop3Def },
  { path: 'retailshops.shop4', def: shop4Def },
  { path: 'retailshops.shop5', def: shop5Def },
  { path: 'retailshops.shop6', def: shop6Def },
  { path: 'retailshops.shop7', def: shop7Def },
  { path: 'retailshops.shop8', def: shop8Def },
  { path: 'retailshops.shop9', def: shop9Def },
] as const;

function hasShopContent(shop: Record<string, string | null>): boolean {
  return !!(shop.name?.trim() || shop.image?.trim() || shop.description?.trim());
}

function ShopCard({
  groupPath,
  definition,
}: {
  groupPath: string;
  definition: SlotGroupDefinition;
}) {
  const shop = useSlotGroup(groupPath, definition);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const filled = hasShopContent(shop);

  if (!filled && !isEditing) return null;

  const name = shop.name?.trim() || (isEditing ? 'Add shop name' : '');
  const href = shop.url?.trim() || '';
  const target = shop._target || '_blank';

  return (
    <article
      data-reveal
      className="reveal-fade-up retailshops-card"
      data-empty={!filled ? 'true' : undefined}
    >
      <EditableSlotGroup groupPath={groupPath} definition={definition}>
        <div className="retailshops-card__inner">
          <div className="retailshops-card__logo">
            {shop.image ? (
              <VibesitesImage
                url={shop.image}
                alt={shop.alt || name || 'Retail shop logo'}
                sizes="(max-width: 768px) 60vw, 280px"
                className="retailshops-card__img"
              />
            ) : (
              <div className="retailshops-card__logo-empty" aria-hidden="true" />
            )}
          </div>

          {href ? (
            <a
              href={href}
              target={target}
              rel={target === '_blank' ? 'noopener noreferrer' : undefined}
              className="retailshops-card__name"
            >
              {name}
            </a>
          ) : (
            <p className="retailshops-card__name">{name}</p>
          )}

          {shop.address?.trim() ? (
            <p className="retailshops-card__address">{shop.address.trim()}</p>
          ) : null}

          {shop.description?.trim() ? (
            <p className="retailshops-card__desc">{shop.description.trim()}</p>
          ) : null}
        </div>
      </EditableSlotGroup>
    </article>
  );
}

/**
 * On-site retail directory — 3-column shop grid with logos, names, and blurbs,
 * modeled after The Village at Odenton Station retail page.
 */
export function RetailShops() {
  useRevealStyles();
  const introRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 80 });

  return (
    <section className="retailshops bg-[var(--vibe-color-background)] py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div ref={introRef} className="reveal-fade-up text-center mb-10 md:mb-14">
          <RichTextContent
            path="retailshops.intro"
            label="Section Intro"
            defaultValue="<p>The Shops at The Village at Odenton Station</p>"
            as="h2"
            className="font-body text-xl sm:text-2xl md:text-[1.65rem] font-normal text-foreground leading-snug"
          />
        </div>

        <div ref={gridRef} className="retailshops-grid">
          {SHOP_DEFS.map(({ path, def }) => (
            <ShopCard key={path} groupPath={path} definition={def} />
          ))}
        </div>

        {/* Schema-lint anchors — literal groupPath required; never interactive */}
        <EditableSlotGroup
          groupPath="retailshops.shop1"
          definition={shop1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop2"
          definition={shop2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop3"
          definition={shop3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop4"
          definition={shop4Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop5"
          definition={shop5Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop6"
          definition={shop6Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop7"
          definition={shop7Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop8"
          definition={shop8Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="retailshops.shop9"
          definition={shop9Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .retailshops-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 2.75rem 2rem;
          align-items: start;
        }

        @media (max-width: 900px) {
          .retailshops-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 2.25rem 1.5rem;
          }
        }

        @media (max-width: 560px) {
          .retailshops-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
            max-width: 22rem;
            margin-inline: auto;
          }
        }

        .retailshops-card[data-empty='true'] .retailshops-card__inner {
          outline: 1px dashed color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          outline-offset: 8px;
          min-height: 10rem;
          opacity: 0.65;
        }

        .retailshops-card__inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0.65rem;
        }

        .retailshops-card__logo {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 16rem;
          height: 7.5rem;
          margin-bottom: 0.35rem;
        }

        .retailshops-card__img {
          display: block;
          max-width: 100%;
          max-height: 7.5rem;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .retailshops-card__logo-empty {
          width: 70%;
          height: 4.5rem;
          background: color-mix(in srgb, var(--vibe-color-text-muted, #9ca3af) 12%, transparent);
        }

        .retailshops-card__name {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          text-decoration: none;
          line-height: 1.3;
        }

        a.retailshops-card__name:hover {
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .retailshops-card__address {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text);
          line-height: 1.4;
        }

        .retailshops-card__desc {
          margin: 0.15rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text);
          line-height: 1.55;
          max-width: 22rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'RetailShops',
  displayName: 'Retail Shops',
  tokens: {
    'retailshops.intro': {
      label: 'Section Intro',
      type: 'richtext',
      defaultValue: '<p>The Shops at The Village at Odenton Station</p>',
      generator: {
        prompt:
          'A short centered heading for the on-site retail directory, e.g. "The Shops at {property name}". Use the real property or plaza name when known.',
        context:
          'Retail page body, above a 3-column grid of tenant logos and blurbs.',
        length: '1 short phrase',
      },
    },
  },
  slotGroups: {
    'retailshops.shop1': shop1Def,
    'retailshops.shop2': shop2Def,
    'retailshops.shop3': shop3Def,
    'retailshops.shop4': shop4Def,
    'retailshops.shop5': shop5Def,
    'retailshops.shop6': shop6Def,
    'retailshops.shop7': shop7Def,
    'retailshops.shop8': shop8Def,
    'retailshops.shop9': shop9Def,
  },
};

(RetailShops as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default RetailShops;
